import { FC, useMemo } from "react";
import { useLocation } from "wouter";

import {
  CreateGUFStartRegistrationTask,
  EditGUFStartRegistrationTask,
} from "@nens/brostar/uploadapi-client";

import { getApi } from "@/api/api-configuration";
import { getFormObjectValues, initializeFormEditingState } from "@/helpers/helpers";
import { BrostarForm, useBrostarFormDataStorage } from "@/libs/brostar-form";
import { GufFormProps } from "@/types/form-types";

import { sanitizeGufEnergyCharacteristicsForSubmit } from "../schema/guf-installation-function-helpers";
import { getGufStartRegistrationSchema } from "../schema/guf-start-registration-schema";
import { useGufStartRegistrationUiSchema } from "../schema/guf-start-registration-ui-schema";
import { validateGufStartRegistrationForm } from "../schema/guf-validation";

const GufStartRegistrationForm: FC<GufFormProps> = (props) => {
  const api = getApi();
  const [, setLocation] = useLocation();

  const { oldUploadTask, task, type } = getFormObjectValues(props);
  const { isEditing, getIsEditing } = initializeFormEditingState(props);

  const { formData, handleChange } = useBrostarFormDataStorage<
    CreateGUFStartRegistrationTask | EditGUFStartRegistrationTask
  >({
    key: "guf-start-registration",
    task,
  });

  const uiSchema = useGufStartRegistrationUiSchema(formData?.metadata.qualityRegime);

  const schema = useMemo(
    () => getGufStartRegistrationSchema({ type, isEditing }),
    [type, isEditing]
  );

  const onSubmit = async () => {
    if (!formData) return;
    const sanitizedFormData = sanitizeGufEnergyCharacteristicsForSubmit(formData);
    getIsEditing(sanitizedFormData)
      ? api.editGUFStartRegistration(sanitizedFormData)
      : api.createGUFStartRegistration(sanitizedFormData);

    if (oldUploadTask?.uuid) api.apiUploadtasksDelete({ uuid: oldUploadTask.uuid });

    setLocation("?viewType=uploadTasks");
  };

  return (
    <BrostarForm<CreateGUFStartRegistrationTask | EditGUFStartRegistrationTask>
      uploadTask={oldUploadTask}
      schema={schema}
      formData={formData}
      onChange={handleChange}
      uiSchema={uiSchema}
      onSubmit={onSubmit}
      customValidate={validateGufStartRegistrationForm}
    />
  );
};

export { GufStartRegistrationForm };
